import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatBRL } from "@/lib/constants";
import {
  Users, ShoppingCart, Activity, CheckCircle2, ShieldAlert,
  Ban, Gavel, ScrollText, Flag, Lock, ClipboardList, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const TABS = [
  { v: "dashboard", l: "Dashboard", icon: Activity, roles: ["admin", "moderator", "privacy"] },
  { v: "users",     l: "Usuários",  icon: Users,   roles: ["admin", "moderator"] },
  { v: "moderation",l: "Moderação", icon: Gavel,   roles: ["admin", "moderator"] },
  { v: "rules",     l: "Regras",    icon: ScrollText, roles: ["admin", "moderator"] },
  { v: "reports",   l: "Denúncias", icon: Flag,    roles: ["admin", "moderator"] },
  { v: "privacy",   l: "Privacidade/LGPD", icon: Lock, roles: ["admin", "privacy"] },
  { v: "audit",     l: "Auditoria", icon: ClipboardList, roles: ["admin", "privacy"] },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (!user) return null;
  const role = user.role;
  const allowed = ["admin", "moderator", "privacy"].includes(role);
  if (!allowed) return <Navigate to="/" replace />;

  const visibleTabs = TABS.filter((t) => t.roles.includes(role));
  const activeTab = visibleTabs.find((t) => t.v === tab) ? tab : visibleTabs[0]?.v;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
      <div className="flex items-center gap-3 mb-6">
        <ShieldAlert className="text-primary" size={28} />
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Painel Admin</h1>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Papel: <span className="text-foreground font-bold">{role}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 bg-secondary p-1 rounded-xl mb-6 w-fit">
        {visibleTabs.map((t) => {
          const I = t.icon;
          const active = activeTab === t.v;
          return (
            <button
              key={t.v}
              data-testid={`admin-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                active ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <I size={16} />
              {t.l}
            </button>
          );
        })}
      </div>

      {activeTab === "dashboard"  && <DashboardTab  />}
      {activeTab === "users"      && <UsersTab currentUserId={user.user_id} isSuperadmin={role === "admin"} />}
      {activeTab === "moderation" && <ModerationTab />}
      {activeTab === "rules"      && <RulesTab />}
      {activeTab === "reports"    && <ReportsTab />}
      {activeTab === "privacy"    && <PrivacyTab />}
      {activeTab === "audit"      && <AuditTab />}
    </div>
  );
}

// -------------------- Dashboard --------------------
function DashboardTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        logger.error("stats failed", e);
        toast.error("Falha ao carregar métricas");
      }
    })();
  }, []);

  const cards = [
    { k: stats?.users, v: "Usuários reais", hint: "Perfis de teste excluídos", icon: Users, tone: "text-primary" },
    { k: stats?.active_7d, v: "Ativos 7d", icon: Activity, tone: "text-emerald-500" },
    { k: stats?.orders_total, v: "Pedidos", icon: ShoppingCart },
    { k: stats?.orders_open, v: "Abertos", icon: CheckCircle2, tone: "text-emerald-500" },
    { k: stats?.orders_pending_review, v: "Em revisão", icon: AlertTriangle, tone: "text-amber-500" },
    { k: stats?.orders_blocked, v: "Bloqueados", icon: Ban, tone: "text-red-500" },
    { k: stats?.orders_completed, v: "Concluídos", icon: CheckCircle2 },
    { k: stats?.reports_open, v: "Denúncias abertas", icon: Flag, tone: "text-amber-500" },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => {
          const I = c.icon;
          return (
            <div key={c.v} className="bg-card border border-border rounded-xl p-4" data-testid={`stat-${c.v.toLowerCase().replace(/\s+/g, '-')}`}>
              <I size={18} className={`mb-2 ${c.tone || "text-muted-foreground"}`} />
              <p className="font-heading font-black text-3xl">{c.k ?? "—"}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.v}</p>
              {c.hint && <p className="text-[10px] text-muted-foreground mt-1">{c.hint}</p>}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        Métricas de usuários reais consideram perfis com e-mail fora dos padrões de teste (+test@, @example.com, @test.com, .test@).
        Total de contas no banco: <b>{stats?.users_all ?? "—"}</b>.
      </p>
    </div>
  );
}

// -------------------- Users --------------------
const ROLE_OPTIONS = ["buyer", "seller", "both", "admin", "moderator", "privacy"];
function UsersTab({ currentUserId, isSuperadmin }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error("Falha ao carregar usuários");
      logger.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const block = async (u) => {
    const blocked = !u.is_blocked;
    if (!window.confirm(`${blocked ? "Bloquear" : "Desbloquear"} ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/block`, { blocked });
      toast.success(blocked ? "Usuário bloqueado" : "Usuário desbloqueado");
      load();
    } catch (e) {
      toast.error("Falha na ação");
      logger.error(e);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`Papel alterado para ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar papel");
      logger.error(e);
    }
  };

  const filtered = users.filter((u) => {
    const t = q.toLowerCase();
    return !t || u.email?.toLowerCase().includes(t) || u.name?.toLowerCase().includes(t);
  });

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-3 border-b border-border">
        <input
          data-testid="users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por e-mail ou nome"
          className="w-full bg-secondary text-sm rounded-lg px-3 py-2 outline-none"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Papel</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Vendas</th>
              <th className="text-left px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-testid={`admin-user-${u.user_id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  {isSuperadmin ? (
                    <select
                      data-testid={`role-select-${u.user_id}`}
                      value={u.role || "buyer"}
                      onChange={(e) => changeRole(u, e.target.value)}
                      disabled={u.user_id === currentUserId}
                      className="bg-secondary rounded px-2 py-1 text-xs"
                    >
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className="capitalize">{u.role}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.is_blocked
                    ? <span className="text-xs font-bold text-red-500">bloqueado</span>
                    : <span className="text-xs text-emerald-500">ativo</span>}
                </td>
                <td className="px-4 py-3">{u.total_sales || 0}</td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`block-btn-${u.user_id}`}
                    onClick={() => block(u)}
                    disabled={u.user_id === currentUserId}
                    className={`text-xs font-bold px-2 py-1 rounded-md border ${
                      u.is_blocked
                        ? "border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                        : "border-red-500 text-red-500 hover:bg-red-500/10"
                    } disabled:opacity-40`}
                  >
                    {u.is_blocked ? "Desbloquear" : "Bloquear"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center text-muted-foreground py-8">Nenhum usuário</p>}
      </div>
    </div>
  );
}

// -------------------- Moderation --------------------
function ModerationTab() {
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/moderation", { params: { status_filter: statusFilter } });
      setOrders(data);
    } catch (e) {
      toast.error("Falha ao carregar fila");
      logger.error(e);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (o, action) => {
    let reason = "";
    if (action !== "approve") {
      reason = window.prompt(action === "block" ? "Motivo do bloqueio:" : "Motivo para revisão:") || "";
      if (!reason.trim()) return;
    }
    try {
      await api.post(`/admin/moderation/${o.order_id}/${action}`, { reason });
      toast.success(action === "approve" ? "Aprovado" : action === "block" ? "Bloqueado" : "Enviado para revisão");
      load();
    } catch (e) {
      toast.error("Falha na ação");
      logger.error(e);
    }
  };

  const FILTERS = [
    { v: "pending_review", l: "Em revisão" },
    { v: "blocked",        l: "Bloqueados" },
    { v: "open",           l: "Aprovados" },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.v}
            data-testid={`mod-filter-${f.v}`}
            onClick={() => setStatusFilter(f.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${
              statusFilter === f.v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.order_id} className="bg-card border border-border rounded-xl p-4" data-testid={`mod-order-${o.order_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <p className="font-heading font-black text-lg">{o.title}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{o.description}</p>
                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                  <span>por <b className="text-foreground">{o.buyer_name}</b></span>
                  <span>{o.category || "—"}</span>
                  <span className="font-mono">{formatBRL(o.max_price)}</span>
                </div>
                {o.moderation_reason && (
                  <p className="text-xs mt-2 text-amber-500">
                    <AlertTriangle size={12} className="inline mr-1" />
                    {o.moderation_reason}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`approve-${o.order_id}`}
                  onClick={() => act(o, "approve")}
                  className="text-xs font-bold px-3 py-1.5 rounded-md border border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                >Aprovar</button>
                <button
                  data-testid={`review-${o.order_id}`}
                  onClick={() => act(o, "review")}
                  className="text-xs font-bold px-3 py-1.5 rounded-md border border-amber-500 text-amber-500 hover:bg-amber-500/10"
                >Revisar</button>
                <button
                  data-testid={`block-${o.order_id}`}
                  onClick={() => act(o, "block")}
                  className="text-xs font-bold px-3 py-1.5 rounded-md border border-red-500 text-red-500 hover:bg-red-500/10"
                >Bloquear</button>
              </div>
            </div>
          </div>
        ))}
        {orders.length === 0 && <p className="text-center text-muted-foreground py-8">Nada na fila</p>}
      </div>
    </div>
  );
}

// -------------------- Rules --------------------
function RulesTab() {
  const [rules, setRules] = useState([]);
  const [term, setTerm] = useState("");
  const [action, setAction] = useState("block");
  const [reason, setReason] = useState("");
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedCount, setSeedCount] = useState(35);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/rules");
      setRules(data);
    } catch (e) { logger.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!term.trim()) return toast.error("Informe um termo");
    try {
      await api.post("/admin/rules", { term: term.trim(), action, reason });
      setTerm(""); setReason("");
      toast.success("Regra adicionada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Desativar regra "${r.term}"?`)) return;
    try {
      await api.delete(`/admin/rules/${r.rule_id}`);
      toast.success("Regra desativada");
      load();
    } catch (err) { logger.error(err); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-primary/30 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <p className="font-heading font-black text-sm uppercase tracking-wider mb-1">
              Demonstrações realistas (Odontologia)
            </p>
            <p className="text-xs text-muted-foreground">
              Gera compradores, anúncios e conversas realistas de odontologia/implantes.
              Marcados como <code className="font-mono">is_demo</code> — não contam nas métricas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number" min={5} max={60}
              value={seedCount}
              onChange={(e) => setSeedCount(parseInt(e.target.value) || 35)}
              className="bg-secondary rounded-lg px-3 py-2 text-sm w-24 outline-none"
              data-testid="seed-count"
            />
            <button
              data-testid="seed-run"
              disabled={seedBusy}
              onClick={async () => {
                if (!window.confirm(`Gerar ${seedCount} anúncios demo? Isso apaga os demos anteriores.`)) return;
                setSeedBusy(true);
                try {
                  const { data } = await api.post("/admin/seed-demo", { count: seedCount });
                  toast.success(`Criados: ${data.created_orders} pedidos + ${data.created_suppliers} fornecedores`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Falha ao gerar");
                } finally { setSeedBusy(false); }
              }}
              className="text-xs font-bold px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            >
              {seedBusy ? "..." : "Gerar demo"}
            </button>
            <button
              data-testid="seed-wipe"
              disabled={seedBusy}
              onClick={async () => {
                if (!window.confirm("Remover TODOS os anúncios demo? Não afeta clientes reais.")) return;
                setSeedBusy(true);
                try {
                  const { data } = await api.post("/admin/seed-demo/wipe");
                  toast.success(`Removidos: ${data.orders} pedidos + ${data.users} usuários`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Falha");
                } finally { setSeedBusy(false); }
              }}
              className="text-xs font-bold px-3 py-2 rounded-md border border-red-500 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
            >
              Limpar demo
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={add} className="bg-card border border-border rounded-xl p-4 grid md:grid-cols-4 gap-3">
        <input
          data-testid="rule-term"
          value={term} onChange={(e) => setTerm(e.target.value)}
          placeholder="Termo (ex: vencido)"
          className="bg-secondary rounded-lg px-3 py-2 text-sm outline-none md:col-span-1"
        />
        <select
          data-testid="rule-action"
          value={action} onChange={(e) => setAction(e.target.value)}
          className="bg-secondary rounded-lg px-3 py-2 text-sm outline-none"
        >
          <option value="block">Bloquear</option>
          <option value="review">Enviar p/ revisão</option>
        </select>
        <input
          data-testid="rule-reason"
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Motivo (opcional)"
          className="bg-secondary rounded-lg px-3 py-2 text-sm outline-none md:col-span-1"
        />
        <button
          data-testid="rule-add-btn"
          type="submit"
          className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-bold"
        >Adicionar</button>
      </form>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Termo</th>
              <th className="text-left px-4 py-3">Ação</th>
              <th className="text-left px-4 py-3">Motivo</th>
              <th className="text-left px-4 py-3">Ativa</th>
              <th className="text-left px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.rule_id} className="border-t border-border">
                <td className="px-4 py-3 font-mono">{r.term}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase font-bold border px-2 py-0.5 rounded-md ${
                    r.action === "block" ? "border-red-500 text-red-500" : "border-amber-500 text-amber-500"
                  }`}>{r.action}</span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{r.reason || "—"}</td>
                <td className="px-4 py-3">{r.active ? "sim" : "não"}</td>
                <td className="px-4 py-3 text-right">
                  {r.active && (
                    <button
                      onClick={() => remove(r)}
                      className="text-xs font-bold text-red-500 hover:underline"
                    >Desativar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rules.length === 0 && <p className="text-center text-muted-foreground py-8">Sem regras customizadas — usando padrões odonto/médico.</p>}
      </div>
    </div>
  );
}

// -------------------- Reports --------------------
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reports", { params: { status_filter: filter } });
      setReports(data);
    } catch (e) { logger.error(e); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    const resolution = window.prompt("Resolução aplicada:") || "resolved";
    try {
      await api.post(`/admin/reports/${r.report_id}/resolve`, { resolution });
      toast.success("Denúncia resolvida");
      load();
    } catch (e) { toast.error("Falha"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "resolved"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${
              filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
            }`}
          >
            {f === "open" ? "Abertas" : "Resolvidas"}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.report_id} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-heading font-black text-sm">{r.reason}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Reporter: {r.reporter_id} · Alvo: {r.target_user_id || r.order_id || "—"}
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">{r.created_at}</p>
              </div>
              {r.status === "open" && (
                <button
                  onClick={() => resolve(r)}
                  className="text-xs font-bold px-3 py-1.5 rounded-md border border-emerald-500 text-emerald-500"
                >Resolver</button>
              )}
            </div>
          </div>
        ))}
        {reports.length === 0 && <p className="text-center text-muted-foreground py-8">Nenhuma denúncia</p>}
      </div>
    </div>
  );
}

// -------------------- Privacy / LGPD --------------------
function PrivacyTab() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reason, setReason] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/privacy/conversations");
        setConvs(data);
      } catch (e) { toast.error("Sem acesso"); }
    })();
  }, []);

  const access = async () => {
    if (reason.trim().length < 10) return toast.error("Motivo deve ter ≥ 10 caracteres");
    try {
      const { data } = await api.post(`/admin/privacy/access-conversation/${selected}`, { reason });
      setMessages(data.messages);
      toast.success("Acesso registrado em auditoria");
    } catch (e) { toast.error("Falha"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-3 border-b border-border font-heading font-black text-sm uppercase tracking-wider">
          Conversas (somente metadados)
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {convs.map((c) => (
            <button
              key={c.order_id}
              onClick={() => { setSelected(c.order_id); setMessages([]); }}
              className={`w-full text-left px-3 py-2 border-b border-border hover:bg-secondary/60 ${
                selected === c.order_id ? "bg-secondary" : ""
              }`}
            >
              <p className="text-xs font-mono">{c.order_id}</p>
              <p className="text-[10px] text-muted-foreground">
                {c.message_count} mensagens · última {c.last_ts?.slice(0, 16)}
              </p>
            </button>
          ))}
          {convs.length === 0 && <p className="text-center text-muted-foreground py-6 text-xs">Sem conversas</p>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-4">
        <p className="font-heading font-black text-sm uppercase tracking-wider mb-3">Acesso auditado (LGPD)</p>
        {!selected && <p className="text-xs text-muted-foreground">Selecione uma conversa à esquerda.</p>}
        {selected && (
          <>
            <p className="text-xs text-muted-foreground mb-2">Pedido: <b className="font-mono">{selected}</b></p>
            <textarea
              data-testid="privacy-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Motivo do acesso (mínimo 10 caracteres). Será registrado no log de auditoria."
              className="w-full bg-secondary rounded-lg px-3 py-2 text-sm outline-none mb-2"
            />
            <button
              onClick={access}
              className="text-xs font-bold px-3 py-2 rounded-md bg-primary text-primary-foreground"
              data-testid="privacy-access-btn"
            >Registrar acesso e ver mensagens</button>
            {messages.length > 0 && (
              <div className="mt-4 max-h-[300px] overflow-y-auto space-y-2">
                {messages.map((m, i) => (
                  <div key={i} className="text-xs bg-secondary rounded p-2">
                    <p className="font-mono text-muted-foreground">{m.sender_id} · {m.created_at?.slice(0, 16)}</p>
                    <p>{m.text}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// -------------------- Audit --------------------
function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/audit", { params: { limit: 200 } });
        setRows(data);
      } catch (e) { logger.error(e); }
    })();
  }, []);
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-3">Quando</th>
            <th className="text-left px-4 py-3">Ator</th>
            <th className="text-left px-4 py-3">Ação</th>
            <th className="text-left px-4 py-3">Alvo</th>
            <th className="text-left px-4 py-3">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.audit_id} className="border-t border-border">
              <td className="px-4 py-3 text-xs text-muted-foreground">{r.ts?.slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.actor_id}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] uppercase font-bold border px-2 py-0.5 rounded-md ${
                  r.action?.includes("block") ? "border-red-500 text-red-500" :
                  r.action?.includes("approve") ? "border-emerald-500 text-emerald-500" :
                  "border-border text-muted-foreground"
                }`}>{r.action}</span>
              </td>
              <td className="px-4 py-3 font-mono text-xs">{r.target || "—"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[240px]">
                {r.meta ? JSON.stringify(r.meta) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-center text-muted-foreground py-8">Sem eventos</p>}
    </div>
  );
}
